import React, { FC, useEffect, useState } from 'react'
import shallow from 'zustand/shallow'
import moment, { Moment } from 'moment-timezone'
import { useTranslation } from 'react-i18next'
import { Col, Divider, Input, Radio, Checkbox, Tooltip } from 'antd'
import { Control, Controller, FieldErrors, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import PerText from '../Text'
import PerSpacer from '../Spacer'
import PerFlexRow from '../FlexRow'
import PerDatePicker from '../DatePicker'
import PerInputNumber from '../PerInputNumber'
import PerButtonCircle from '../ButtonCircle'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { TargetsModalComment } from './TargetsModalComment'
import { MaxCharactersWrapper } from '../../formik-component/MaxCharactersWrapper'
import { useTargetsModalStore } from './TargetsModalStore'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { TargetsModalAttachment } from './TargetsModalAttachment'
import { TargetsModalCommentInput } from './TargetsModalCommentInput'
import { TargetModalTargetType, ObjectiveExtended } from './types'
import { ObjectiveStatus, KeyResultDto, Currency, Country, ObjectiveTemplateDto, ObjectiveInput } from '../../../types'
import './index.less'
import { TargetsModalAsterisk, TargetsModalMessage } from './TargetsModalMessageComponents'
import classNames from 'classnames'
import { useTargetsTemplates } from './async'
import { TargetsTemplateSelect } from './TargetsTemplateSelect'
import { round2digits } from '../../../modules/appraisal/appraisal-flow/meeting/util/paymentsUtil'
import RichTextBox from '../RichTextBox'

const MAX_CHARS = 4000

const { TextArea } = Input

const getTasksLength = (tasks: Array<KeyResultDto>) => tasks.map((task) => task.text).join('').length

const TaskCharacterCounter = ({ control }: { control: Control<ObjectiveInput> }) => {
  const { t } = useTranslation()
  const tasks = useWatch({ control, name: 'keyResults' })
  return !!tasks && !!tasks.length ? (
    <PerText fontSize="xs" color="secondary" align="right" mt="xxxs">
      {`${t('targetModal.totalOfCharInTasks')} ${getTasksLength(tasks)} / ${MAX_CHARS}`}
    </PerText>
  ) : null
}

export const TargetsModalFormNewTarget: FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatCurrency } = useAmountAndCurrency(user.locale)
  const { utcDateStringToMoment, calcStartOfNextNYear, calcEndOfNextNYear, calcStartOfTheYearMoment, calcEndOfTheYearMoment } =
    useDateTime(user)

  const [currentObjective, currentComments, setNewTargetForm, modalProps] = useTargetsModalStore(
    (state) => [state.currentObjective, state.currentComments, state.setNewTargetForm, state.modalProps],
    shallow,
  )

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { data } = useTargetsTemplates(modalProps.employeeId!)
  const targetsTemplate: ObjectiveTemplateDto[] = data && data.getTargetsTemplates ? data.getTargetsTemplates : []

  const { meeting } = modalProps

  const _targetType =
    !!currentObjective && currentObjective.targetAmount ? TargetModalTargetType.monetary : TargetModalTargetType.development
  const [targetType, setTargetType] = useState<TargetModalTargetType>(_targetType)

  const newTargetForm = useForm<ObjectiveExtended>({
    defaultValues: !!currentObjective
      ? {
          ...currentObjective,
          targetType: _targetType,
          achievedAmountPercentage:
            !!currentObjective.targetAmount && (!!currentObjective.achievedAmount || currentObjective.achievedAmount === 0)
              ? Number(((currentObjective.achievedAmount / currentObjective.targetAmount) * 100).toFixed(2))
              : undefined,
          markedAsDone: !!currentObjective.targetAmount
            ? !!currentObjective.achievedAmount || currentObjective.achievedAmount === 0
            : currentObjective.status === ObjectiveStatus.MarkedAsDone,
        }
      : {
          keyResults: [{}],
          start: !!meeting ? calcStartOfTheYearMoment(meeting.appraisalSeason.year).toISOString() : calcStartOfNextNYear(0).toISOString(),
          due: !!meeting ? calcEndOfTheYearMoment(meeting.appraisalSeason.year).toISOString() : calcEndOfNextNYear(0).toISOString(),
          status: ObjectiveStatus.ProposalFromManager,
          targetType: _targetType,
        },
    criteriaMode: 'all',
    mode: 'onChange',
  })

  const {
    control,
    formState: { errors },
    getValues,
    trigger,
    unregister,
    setValue,
    watch,
    clearErrors,
  } = newTargetForm

  const { fields, remove, append, replace } = useFieldArray({
    name: 'keyResults',
    keyName: 'id',
    control,
  })

  const currency = modalProps.employeeCurrency || Currency.Eur

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const hasValidSelection = e.currentTarget.selectionStart !== e.currentTarget.selectionEnd
    if (getTasksLength(getValues().keyResults) >= MAX_CHARS) {
      if (!hasValidSelection) {
        e.preventDefault()
      }
    }
  }

  const handleOnPaste = (e: React.ClipboardEvent) => {
    const currentChars = getTasksLength(getValues().keyResults)
    if (currentChars + e.clipboardData.getData('text/plain').length >= MAX_CHARS) {
      e.preventDefault()
    }
  }

  const hasError = (errors: FieldErrors<ObjectiveExtended>, fieldName: string, index: number) => {
    if (index > -1) {
      return !!errors?.keyResults?.[index]?.[fieldName]
    }
    return !!errors && !!errors[fieldName]
  }

  const calcThreshold = () => {
    const targetAmount = !!currentObjective && currentObjective.targetAmount ? currentObjective.targetAmount : 0
    const targetBonus = !!modalProps.targetBonus ? modalProps.targetBonus : 0
    const alreadyPlanned = !!modalProps.alreadyPlanned ? modalProps.alreadyPlanned : 0
    const remainingBonus = targetBonus - alreadyPlanned
    const threshold = targetAmount + remainingBonus
    return round2digits(threshold)
  }

  const calcRemainingBonus = (currentValue: number | null | undefined) => {
    const value = currentValue || 0
    return calcThreshold() - value
  }

  const calcAchievedAmount = (targetAmount: number | null | undefined, achievedAmountPercentage: number | undefined) => {
    if (!!targetAmount && (!!achievedAmountPercentage || achievedAmountPercentage === 0)) {
      setValue('achievedAmount', Number(((achievedAmountPercentage / 100) * targetAmount).toFixed(2)))
    } else {
      setValue('achievedAmount', undefined)
    }
  }

  const calcAchievedAmountPercentage = (achievedAmount: number | null | undefined, targetAmount: number | null | undefined) => {
    if (!!targetAmount && (!!achievedAmount || achievedAmount === 0)) {
      setValue('achievedAmountPercentage', Number(((achievedAmount / targetAmount) * 100).toFixed(2)))
    } else {
      setValue('achievedAmountPercentage', undefined)
    }
  }

  const handleTargetAmountChange = () => {
    if (!getValues().targetAmount) {
      setValue('achievedAmount', undefined)
      setValue('achievedAmountPercentage', undefined)
    } else {
      if (!!getValues().achievedAmountPercentage) {
        calcAchievedAmount(getValues().targetAmount, getValues().achievedAmountPercentage)
      }
    }
  }

  useEffect(() => {
    setNewTargetForm(newTargetForm)
    trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (targetType === TargetModalTargetType.monetary) {
      trigger()
    }
  }, [targetType, trigger, unregister])

  watch(['targetAmount', 'keyResults', 'markedAsDone', 'achievedAmount', 'achievedAmountPercentage'])

  return (
    <>
      <TargetsTemplateSelect targetsTemplate={targetsTemplate} setTitle={setValue} clearErrors={clearErrors} replaceTasks={replace} />

      <PerFlexRow direction="column">
        <PerText appearance="label-small" mb="xxs">
          {t('targetModal.title')}
        </PerText>
        <Controller
          name="title"
          control={control}
          rules={{ required: `${t('formError.generalRequired')}` }}
          render={({ field: { onChange, value } }) => (
            <>
              <div className={classNames({ 'has-error': hasError(errors, 'title', -1) })} style={{ width: '100%' }}>
                <TextArea
                  autoComplete="off"
                  value={value || undefined}
                  onChange={onChange}
                  rows={1}
                  maxLength={MAX_CHARS}
                  style={{ width: '100%', lineHeight: 1.6 }}
                  placeholder={t('targetModal.titlePlaceholder')}
                  data-e2e-target-title
                />
              </div>
              <PerFlexRow align="middle" justify="space-between" className="fullWidth">
                <PerFlexRow justify="start">
                  <ErrorMessage
                    errors={errors}
                    name="title"
                    render={({ messages }) => {
                      return (
                        <PerText fontSize="xs" color="error">
                          {!!messages && !!messages.required && messages.required}
                        </PerText>
                      )
                    }}
                  />
                </PerFlexRow>
                <PerFlexRow justify="end">
                  <MaxCharactersWrapper usedCharacters={!!value ? value.length : 0} maxCharacters={MAX_CHARS} />
                </PerFlexRow>
              </PerFlexRow>
            </>
          )}
        />
      </PerFlexRow>

      <PerFlexRow mt="s">
        <PerText appearance="tile-heading">{t('targetModal.tasks')}</PerText>
      </PerFlexRow>

      <PerFlexRow direction="column">
        {fields.map((field, index) => {
          return (
            <React.Fragment key={index}>
              <PerFlexRow key={field.id || field.globalId} className="fullWidth per-target-modal--tasks-wrapper" align="middle" mt="s">
                <PerFlexRow
                  className={classNames({
                    'per-target-modal--tasks-checkbox top-fix': index === fields.length - 1,
                    'per-target-modal--tasks-checkbox': index !== fields.length,
                  })}
                >
                  <Controller
                    name={`keyResults.${index}.completed`}
                    control={control}
                    defaultValue={false}
                    render={({ field: { onChange, value } }) => {
                      return (
                        <PerFlexRow className="per-formikKeyResultsItem">
                          <Tooltip title={t('targetModal.taskCheckboxTooltip')} placement="left">
                            <Checkbox
                              checked={value}
                              onChange={(e) => {
                                const checked = e.target.checked
                                onChange(checked)
                                if (getValues().keyResults.every((task) => task.completed)) {
                                  setValue('markedAsDone', true)
                                }
                              }}
                            />
                          </Tooltip>
                        </PerFlexRow>
                      )
                    }}
                  />
                </PerFlexRow>
                <PerFlexRow className="per-target-modal--tasks-input">
                  <PerFlexRow className="fullWidth" align="middle" justify="start" noWrap>
                    <Controller
                      name={`keyResults.${index}.text`}
                      control={control}
                      rules={{
                        required: `${t('formError.generalRequired')}`,
                        minLength: { value: 2, message: `${t('formError.textLength')}` },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <div className={classNames({ 'has-error': hasError(errors, 'text', index) })} style={{ width: '89%' }}>
                          <TextArea
                            value={value || undefined}
                            onChange={onChange}
                            rows={1}
                            style={{ width: '100%', lineHeight: 1.6 }}
                            onKeyPress={handleKeyPress}
                            onPaste={handleOnPaste}
                            placeholder={t('targetModal.taskPlaceholder')}
                            data-e2e-keyresult-input
                          />
                        </div>
                      )}
                    />
                    <PerFlexRow align="middle" justify="start">
                      {fields.length > 1 && (
                        <PerButtonCircle
                          icon="remove_stroke"
                          size="small"
                          variant="pastel"
                          color="secondary"
                          ml="xs"
                          onClick={() => remove(index)}
                          data-e2e-keyresult-button="remove"
                        />
                      )}
                      {index === fields.length - 1 && (
                        <PerButtonCircle
                          icon="add_stroke"
                          size="small"
                          variant="pastel"
                          color="success"
                          ml="xxs"
                          disabled={!!errors.keyResults}
                          onClick={() => {
                            append({ text: '' } as KeyResultDto)
                            trigger()
                          }}
                          data-e2e-keyresult-button="add"
                        />
                      )}
                    </PerFlexRow>
                  </PerFlexRow>
                  <PerFlexRow align="middle" justify="space-between" width="89%">
                    <PerFlexRow align="middle" justify="start">
                      <PerText fontSize="xs" color="error" mt="xxxs">
                        <ErrorMessage
                          errors={errors}
                          name={`keyResults.${index}.text`}
                          render={({ messages }) => {
                            return (
                              <>
                                <PerText fontSize="xs" color="error">
                                  {!!messages && !!messages.required && messages.required}
                                </PerText>
                                <PerText fontSize="xs" color="error">
                                  {!!messages && !!messages.minLength && messages.minLength}
                                </PerText>
                              </>
                            )
                          }}
                        />
                      </PerText>
                    </PerFlexRow>
                    <PerFlexRow align="middle" justify="end">
                      {index === fields.length - 1 && <TaskCharacterCounter control={control} />}
                    </PerFlexRow>
                  </PerFlexRow>
                </PerFlexRow>
              </PerFlexRow>
            </React.Fragment>
          )
        })}
      </PerFlexRow>

      <PerFlexRow mt="xl">
        <PerText appearance="tile-heading">{t('targetModal.notes')}</PerText>
      </PerFlexRow>
      <PerFlexRow direction="column" mt="m">
        <Controller
          name="taskNotes"
          control={control}
          render={({ field: { onChange, value } }) => (
            <RichTextBox placeholder={t('targetModal.notesPlaceholder')} text={value || ''} onChange={onChange} />
          )}
        />
      </PerFlexRow>

      <Divider />

      <PerFlexRow>
        <PerText appearance="tile-heading">{t('targetModal.targetDetails')}</PerText>
      </PerFlexRow>

      <PerFlexRow mt="m">
        <Controller
          name="targetType"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Radio.Group
              value={value}
              onChange={(e) => {
                onChange(e)
                setTargetType(e.target.value)
              }}
            >
              <Radio value={TargetModalTargetType.development} data-e2e-new-target-radio-development>
                <PerText fontWeight="bold" display="inline-block">
                  {t('targetModal.developmentTarget')}
                </PerText>
              </Radio>
              <Tooltip title={modalProps.disabledMonetaryButtonTooltip}>
                <Radio
                  value={TargetModalTargetType.monetary}
                  disabled={!modalProps.allowedNewTargets || !modalProps.targetBonus || !modalProps.hasBonusOrWillHaveBonus}
                  data-e2e-new-target-radio-monetary
                >
                  <PerText fontWeight="bold" display="inline-block">
                    {t('targetModal.monetaryTarget')}
                  </PerText>
                </Radio>
              </Tooltip>
            </Radio.Group>
          )}
        />
      </PerFlexRow>

      <PerFlexRow mt="m">
        <Col span={8}>
          <PerText appearance="label-small" mb="xxs">
            {t('targetModal.startDate')}
          </PerText>
          <Controller
            name="start"
            control={control}
            rules={{ required: `${t('formError.generalRequired')}`, deps: ['due'] }}
            render={({ field: { onChange, value } }) => (
              <PerDatePicker
                showToday={false}
                inputDate={value}
                dateFormat="DMY"
                timeZoneAndLocale={user}
                size="default"
                width={'100%'}
                disabledDate={(current) =>
                  !!meeting &&
                  !!current &&
                  (current.isBefore(calcStartOfTheYearMoment(meeting.appraisalSeason.year), 'year') ||
                    current.isAfter(calcEndOfTheYearMoment(meeting.appraisalSeason.year), 'year'))
                }
                className={classNames({ 'has-error': hasError(errors, 'start', -1) })}
                onDateChanged={(date) => {
                  onChange(date)
                }}
                keepTimezoneOffset={true}
              />
            )}
          />
          <div className="per-target-modal--field-error">
            <ErrorMessage
              errors={errors}
              name="start"
              render={({ messages }) => {
                return (
                  <>
                    <PerText fontSize="xs" color="error">
                      {!!messages && !!messages.required && messages.required}
                    </PerText>
                  </>
                )
              }}
            />
          </div>
        </Col>
        <Col span={1} />
        <Col span={8}>
          <PerText appearance="label-small" mb="xxs">
            {t('targetModal.dueDate')}
          </PerText>
          <Controller
            name="due"
            control={control}
            rules={{
              required: `${t('formError.generalRequired')}`,
              validate: (val) => {
                return moment(val) > moment(getValues().start).endOf('day') || `${t('formError.dueDateBefore')}`
              },
            }}
            render={({ field: { onChange, value } }) => (
              <PerDatePicker
                showToday={false}
                inputDate={value}
                dateFormat="DMY"
                timeZoneAndLocale={user}
                size="default"
                width={'100%'}
                disabledDate={(current: Moment | null) =>
                  !!meeting &&
                  !!current &&
                  (current < moment(getValues().start).endOf('day') ||
                    current.isAfter(calcEndOfTheYearMoment(meeting.appraisalSeason.year), 'year'))
                }
                className={classNames({ 'has-error': hasError(errors, 'due', -1) })}
                onDateChanged={(date) => onChange(date)}
                keepTimezoneOffset={true}
              />
            )}
          />
          <div className="per-target-modal--field-error">
            <ErrorMessage
              errors={errors}
              name="due"
              render={({ messages }) => {
                return (
                  <>
                    <PerText fontSize="xs" color="error">
                      {!!messages && !!messages.required && messages.required}
                    </PerText>
                    <PerText fontSize="xs" color="error">
                      {!!messages && !!messages.validate && messages.validate}
                    </PerText>
                  </>
                )
              }}
            />
          </div>
        </Col>
      </PerFlexRow>

      {targetType === TargetModalTargetType.monetary && !!modalProps.targetBonus && (
        <Controller
          name="targetAmount"
          control={control}
          rules={{
            required: `${t('formError.generalRequired')}`,
            max: { value: calcThreshold(), message: `${t('formError.targetAmount')}` },
          }}
          render={({ field: { onChange, value } }) => (
            <>
              <PerFlexRow mt="xs" align="middle">
                <Col span={8}>
                  <PerText appearance="label-small" mb="xxs">
                    {t('targetModal.targetAmount')}
                    {modalProps.employeeCountry && modalProps.employeeCountry === Country.Pol && <TargetsModalAsterisk />}
                  </PerText>
                  <div className={classNames({ 'has-error': hasError(errors, 'targetAmount', -1) })} style={{ width: '100%' }}>
                    <PerInputNumber
                      type="currency"
                      value={value as number | undefined}
                      onChange={(value) => {
                        onChange(value)
                        handleTargetAmountChange()
                      }}
                      width={'100%'}
                      currency={
                        (currentObjective &&
                          !!currentObjective.targetAmount &&
                          currentObjective.targetAmount > 0 &&
                          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                          currentObjective.currency!) ||
                        currency
                      }
                      placeholder="0"
                      min={0}
                      timezoneAndLocale={user}
                      data-e2e-monetary-input
                    />
                  </div>
                </Col>
                <Col span={1} />
                <Col span={8}>
                  <PerFlexRow direction="column" mt="m">
                    <PerFlexRow>
                      <PerText appearance="label-small">{t('targetModal.annualBonus')}</PerText>
                      <PerText appearance="label-small" color="default" ml="xxxs">
                        {!!modalProps.targetBonus && formatCurrency(modalProps.targetBonus, currency, true)}
                      </PerText>
                    </PerFlexRow>
                    <PerFlexRow>
                      <PerText appearance="label-small">{t('targetModal.remainingBonus')}</PerText>
                      <PerText appearance="label-small" color={calcRemainingBonus(value) < 0 ? 'error' : 'default'} ml="xxxs">
                        {!!modalProps.targetBonus && formatCurrency(calcRemainingBonus(value), currency, true)}
                      </PerText>
                    </PerFlexRow>
                  </PerFlexRow>
                </Col>
              </PerFlexRow>
              {errors && errors['targetAmount'] && (
                <div className="per-target-modal--field-error">
                  <ErrorMessage
                    errors={errors}
                    name="targetAmount"
                    render={({ messages }) => {
                      return (
                        <>
                          <PerText fontSize="xs" color="error">
                            {!!messages && !!messages.required && messages.required}
                          </PerText>
                          <PerText fontSize="xs" color="error">
                            {!!messages && !!messages.max && messages.max}
                          </PerText>
                        </>
                      )
                    }}
                  />
                </div>
              )}
              {modalProps.employeeCountry && modalProps.employeeCountry === Country.Pol && (
                <TargetsModalMessage message={t(`targetModal.targetMessage.${Country.Pol}`)} />
              )}
            </>
          )}
        />
      )}

      <PerFlexRow mt="xl" mb="l" align="middle">
        <Controller
          name="markedAsDone"
          control={control}
          defaultValue={false}
          render={({ field: { onChange, value } }) => (
            <>
              <PerFlexRow className="per-formikKeyResultsItem">
                <Checkbox
                  checked={value}
                  onChange={(e) => {
                    onChange(e)
                    trigger()
                  }}
                  data-testid="markAsDoneCheckbox"
                  disabled={getValues().keyResults.every((task) => task.completed)}
                >
                  {t('targetModal.markAsDone')}
                </Checkbox>
              </PerFlexRow>
            </>
          )}
        />
      </PerFlexRow>

      {targetType === TargetModalTargetType.monetary && getValues().markedAsDone && (
        <PerFlexRow mt="xs" align="middle">
          <Col span={8}>
            <PerText appearance="label-small" mb="xxs">
              {t('targetModal.achievedAmount')}
            </PerText>
            <Controller
              name="achievedAmount"
              control={control}
              rules={{
                required: { value: !!getValues().markedAsDone, message: `${t('targetModal.achievedAmountRequired')}` },
              }}
              render={({ field: { onChange, value } }) => (
                <>
                  <div className={classNames({ 'has-error': hasError(errors, 'achievedAmount', -1) })} style={{ width: '100%' }}>
                    <PerInputNumber
                      type="currency"
                      value={value as number | undefined}
                      onChange={(value) => {
                        onChange(value)
                        calcAchievedAmountPercentage(value, getValues().targetAmount)
                      }}
                      disabled={!getValues().markedAsDone}
                      width={'100%'}
                      currency={currency}
                      min={0}
                      timezoneAndLocale={user}
                    />
                  </div>
                  <div className="per-target-modal--field-error">
                    <ErrorMessage
                      errors={errors}
                      name="achievedAmount"
                      render={({ messages }) => {
                        return (
                          <>
                            <PerText fontSize="xs" color="error">
                              {!!messages && !!messages.required && messages.required}
                            </PerText>
                          </>
                        )
                      }}
                    />
                  </div>
                </>
              )}
            />
          </Col>
          <Col span={1} />
          <Col span={8}>
            <PerFlexRow mt="m" align="middle">
              <Controller
                name="achievedAmountPercentage"
                control={control}
                rules={{ deps: ['achievedAmount'] }}
                render={({ field: { onChange, value } }) => (
                  <>
                    <div>
                      <PerInputNumber
                        type="percentage"
                        value={value}
                        onChange={(value) => {
                          onChange(value)
                          calcAchievedAmount(getValues().targetAmount, value)
                        }}
                        disabled={!getValues().markedAsDone}
                        width={'100%'}
                        min={0}
                        percentageHasDecimal
                        timezoneAndLocale={user}
                      />
                    </div>
                    <div className="per-target-modal--field-error"></div>
                  </>
                )}
              />
            </PerFlexRow>
          </Col>
        </PerFlexRow>
      )}

      <PerSpacer mt="m" />

      <TargetsModalAttachment showDropZone />

      {meeting && currentObjective && user.employeeId !== meeting.employee.id && currentObjective.status === ObjectiveStatus.Proposal ? (
        <>
          <Divider />

          <PerFlexRow direction="column">
            <PerText appearance="tile-heading" mb="xxs">
              {t('targetModal.comments')}
            </PerText>
            <PerText fontSize="xs" color="secondary">
              {t('targetModal.commentsReviewerDisclaimer')}
            </PerText>
          </PerFlexRow>

          {currentComments
            .slice()
            .sort((a, b) => {
              const date1 = utcDateStringToMoment(a.date)
              const date2 = utcDateStringToMoment(b.date)
              return date1 && date2 && date1.isBefore(date2) ? -1 : date1 && date2 && date1.isAfter(date2) ? 1 : 0
            })
            .map((note, index) => {
              return <TargetsModalComment comment={note} index={index} key={`${index}${note.date}`} />
            })}

          {!meeting.managerPrepared && <TargetsModalCommentInput />}
        </>
      ) : (
        <></>
      )}
    </>
  )
}
